// Composer Studio — 식별 키 중복 경고 옆의 "사용처" 목록.
//
// 경고는 "이미 다른 노드에서 사용 중인 키입니다"라고만 말한다. 캔버스 강조(sharedKeyHighlight)가
// 어디인지 보여 주지만 두 경우에 안 보인다: 매칭이 **뷰포트 밖**이거나 **비활성 분기 슬롯 안**
// (DOM 부재라 rect가 없다). 그래서 겹치는 자리를 글로 나열하고, 항목에 커서를 올리면 캔버스가 그
// 자리로 스크롤 + 강조된다.
//
// ⚠️ **hover=파악(뷰 불변) / click=도달(슬롯 전환+스크롤).** 둘 다 **선택은 바꾸지 않는다** — 항목
// 클릭이 선택을 옮기면 저작 중이던 자리를 잃는다(겹침을 확인하려던 것뿐인데 편집 대상이 바뀐다).
// 클릭을 no-op으로 두었던 초안(2026-09-03)은 그 이유로 옳았지만, 그러면 **비활성 슬롯 안 자리에는
// 닿을 방법이 아예 없었다** — 검출은 정적 분석이라 그런 자리까지 잡는데 도달 수단만 끊겨 있었다.
// 그래서 클릭은 선택 대신 **분기 슬롯 탭 전환**으로 도달시킨다(channel.revealOwner).
// 키보드 사용자를 위해 focus/blur도 hover와 같은 채널을 태운다(Enter는 클릭과 같다).
//
// 배치: 세 경고 컨텍스트(분기 조건 키 · BindingEditor 텍스트 입력 · 행 필드 콤보)가 **같은**
// 어포던스를 갖도록 이 컴포넌트 하나만 경고 아래에 둔다(HybridKeyPicker 내부는 건드리지 않는다).

import { useContext, useEffect, useState } from 'react';
import {
  SharedKeyFocusContext,
  isOwnerOnCanvas,
  type SharedKeyFocusChannel,
  type SharedKeyOwnerDetail,
} from './sharedKeyHighlight';

/**
 * @param bindingKey 지금 편집 중인 식별 키(빈 문자열이면 아무것도 렌더하지 않는다).
 * @param selfOwnerId 이 편집기가 대표하는 자리(`node:…`/`cell:…`) — 자기 자신은 목록에서 빠진다.
 *   캔버스에 대응 자리가 없는 초안/일괄 패널에서는 undefined라 목록도 뜨지 않는다(강조와 같은 규칙).
 */
export function SharedKeyUsageList({
  bindingKey,
  selfOwnerId,
}: {
  bindingKey: string;
  selfOwnerId?: string;
}) {
  const channel = useContext(SharedKeyFocusContext);
  const [open, setOpen] = useState(false);

  // 목록이 열린 채 패널이 사라져도 강조가 남지 않게 — 언마운트는 mouseleave가 오지 않는 경로다.
  useEffect(() => () => channel?.setHoverOwner(null), [channel]);

  // 매 렌더 재계산(메모 없음) — 저작 중 body가 바뀌면 목록도 그 자리에서 따라가야 한다. 속성
  // 패널의 키 행 하나에서만 돌고 body 1회 훑기라 비용이 문제되지 않는다.
  const details =
    channel && selfOwnerId && bindingKey ? channel.getOwnerDetails(bindingKey, selfOwnerId) : [];
  if (!channel || details.length === 0) return null;

  // 절 분리 — "함께 표시되는 자리"(진짜 중복 후보)와 "다른 분기 슬롯의 같은 필드"(상호배타,
  // 값만 공유)는 저작자에게 의미가 다르다. 후자를 경고 아래 같은 목록에 섞으면 중복의 증거처럼
  // 읽힌다(2026-09-03 사용자 지적 — tml-basic-title의 단일회차 슬롯 자리).
  const concurrent = details.filter((d) => !d.mutuallyExclusive);
  const exclusive = details.filter((d) => d.mutuallyExclusive);

  return (
    <div className="mt-0.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="text-[11px] text-brand-600 underline decoration-dotted hover:text-brand-700"
        title="같은 키를 쓰는 자리 목록 — 커서를 올리면 캔버스가 그 자리로 이동하고, 클릭하면 분기 슬롯까지 전환해 보여 줍니다(선택은 그대로)"
      >
        사용처 {details.length}곳 {open ? '접기' : '보기'}
      </button>
      {open && (
        <div className="mt-0.5 space-y-1 rounded border border-gray-200 bg-white p-1">
          {concurrent.length > 0 && (
            <section aria-label="함께 표시되는 자리">
              {/* 절이 하나뿐이면 머리글 생략 — 구분할 상대가 없는 머리글은 소음이다. */}
              {exclusive.length > 0 && (
                <p className="px-1 text-[10px] font-medium text-gray-500">함께 표시되는 자리</p>
              )}
              <UsageItems channel={channel} items={concurrent} showSlotSuffix />
            </section>
          )}
          {exclusive.length > 0 && (
            <section aria-label="다른 분기 슬롯의 같은 필드">
              {/* 이 절은 항상 머리글을 단다 — "왜 경고 없이 목록에 있나"의 답이 머리글이다. */}
              <p className="px-1 text-[10px] font-medium text-gray-500">
                다른 분기 슬롯의 같은 필드 — 함께 표시되진 않지만 값은 공유
              </p>
              <UsageItems channel={channel} items={exclusive} showSlotSuffix={false} />
            </section>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * 사용처 항목 묶음 — hover/focus는 캔버스 스크롤+강조(파악), 클릭은 도달(슬롯 전환+스크롤).
 * 둘 다 선택은 불변이다.
 * showSlotSuffix: 캔버스에 없는 항목의 사유 표기. 배타 절은 머리글이 이미 사유를 말하므로 끈다.
 */
function UsageItems({
  channel,
  items,
  showSlotSuffix,
}: {
  channel: SharedKeyFocusChannel;
  items: SharedKeyOwnerDetail[];
  showSlotSuffix: boolean;
}) {
  return (
    <ul className="space-y-0.5">
      {items.map((d) => {
        const onCanvas = isOwnerOnCanvas(d.ownerId);
        const hover = () => channel.setHoverOwner(d.ownerId);
        const clear = () => channel.setHoverOwner(null);
        return (
          <li key={d.ownerId}>
            <button
              type="button"
              onMouseEnter={hover}
              onMouseLeave={clear}
              onFocus={hover}
              onBlur={clear}
              onClick={() => channel.revealOwner(d.ownerId)}
              title="클릭하면 그 자리로 이동합니다(분기 슬롯도 전환 — 선택은 그대로)"
              className={`block w-full text-left text-[11px] font-mono leading-snug rounded px-1 py-0.5 hover:bg-brand-50 ${
                onCanvas ? 'text-gray-600' : 'text-gray-400'
              }`}
            >
              {d.label}
              {!onCanvas && showSlotSuffix && (
                <span className="ml-1 font-sans text-gray-400">
                  {d.branchSlotLabels.length > 0
                    ? '(다른 분기 슬롯 — 클릭하면 전환)'
                    : '(캔버스에 표시되지 않음)'}
                </span>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
